"use client";

import React, { useEffect, useRef, useState } from "react";
import { PhoneMissed, Zap, UserCheck, Inbox } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const steps = [{
  title: "Call Missed",
  description: "A potential customer calls while you're 'on the job'\xA0 or driving between jobs. In the past, that missed call was a lost lead to a competitor.",
  icon: PhoneMissed
}, {
  title: "Instant Text-Back",
  description: "Within seconds, the Silent Operator triggers an automated WhatsApp or Instant WhatsApp Recovery response: 'Sorry we missed you! How can we help today?'",
  icon: Zap
}, {
  title: "Lead Secured",
  description: "The customer replies instantly, stopping their search for another firm. Your lead is captured and secured before you've even finished your current task.",
  icon: UserCheck
}, {
  title: "Unified Inbox",
  description: "All responses flow into one simple app. Manage your WhatsApp, Instant WhatsApp Recovery, and Google messages in one place without losing track of your next big project.",
  icon: Inbox
}];
function StepItem({
  step,
  index
}: {
  step: (typeof steps)[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.3
  });
  const isEven = index % 2 === 0;
  return <div data-section-id="92560" data-index={index} ref={ref} className={`relative w-full mb-24 last:mb-0 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-16"}`}>
      <div className={`absolute left-4 md:left-1/2 top-0 -translate-x-1/2 flex items-center justify-center w-12 h-12 rounded-full border-4 border-background bg-muted z-10 transition-all duration-700 delay-300 ${isVisible ? "border-primary/30 shadow-lg shadow-primary/20" : "border-transparent shadow-none"}`}>
        <step.icon className={`w-5 h-5 transition-colors duration-700 delay-300 ${isVisible ? "text-primary" : "text-muted-foreground"}`} />
      </div>

      <div className={`ml-16 md:ml-0 md:w-1/2 pt-1 ${isEven ? "md:pr-16 md:text-right" : "md:pl-16 md:ml-auto"}`}>
        <Card className="glass-panel metallic-border rounded-[4px] hover:border-primary/40 transition-all duration-500 hover:shadow-lg hover:shadow-primary/5">
          <CardHeader className="pb-4">
            <div className={`text-primary font-sans text-sm font-bold tracking-wider uppercase mb-2 ${isEven ? "md:text-right" : "text-left"}`}>
              Step 0{index + 1}
            </div>
            <CardTitle className={`h3 text-foreground ${isEven ? "md:text-right" : "text-left"}`}>
              {step.title}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className={`text-muted-foreground leading-relaxed font-sans ${isEven ? "md:text-right" : "text-left"}`}>
              {step.description}
            </p>
          </CardContent>
        </Card>
      </div>
    </div>;
}
export default function OurProprietaryProcess() {
  const [progress, setProgress] = useState(0);
  const timelineRef = useRef<HTMLDivElement>(null);
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  useEffect(() => {
    const handleScroll = () => {
      if (!timelineRef.current) return;
      const rect = timelineRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const middle = windowHeight / 2;
      let p = (middle - rect.top) / rect.height * 100;
      p = Math.max(0, Math.min(100, p));
      setProgress(p);
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  return <section id="how-it-works" className="dark bg-background text-foreground py-24 md:py-32 relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none radiating-pattern">
        <div className="absolute top-1/4 left-[-10%] w-[40%] h-[40%] bg-primary/5 rounded-full blur-[120px]" />
        <div className="absolute bottom-1/4 right-[-10%] w-[40%] h-[40%] bg-primary/5 rounded-full blur-[120px]" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div ref={headerRef} className={`text-center max-w-3xl mx-auto mb-24 transition-all duration-1000 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          <h2 className="h2 mb-6 text-foreground">
            The <span className="text-primary">Silent Operator</span> In Action
          </h2>
          <p className="text-lg text-muted-foreground font-sans">
            The system that sends an instant WhatsApp to your missed calls.
          </p>
        </div>

        <div ref={timelineRef} className="relative max-w-5xl mx-auto">
          <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-1 bg-muted -translate-x-1/2 rounded-full" />

          <div className="absolute left-4 md:left-1/2 top-0 w-1 bg-primary -translate-x-1/2 rounded-full transition-all duration-75 ease-linear" style={{
          height: `${progress}%`
        }}>
            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 w-3 h-3 rounded-full bg-primary shadow-lg shadow-primary/50" />
          </div>

          <div className="relative">
            {steps.map((step, i) => <StepItem key={i} step={step} index={i} />)}
          </div>
        </div>
      </div>
    </section>;
}